import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DollarSign, Landmark, Calendar, FileText, ArrowLeft, Lock } from 'lucide-react';
import './RegistrationStyles.css';

const url = process.env.REACT_APP_BACKEND_URL;
const FIXED_FEE = 10.00;

const EditRegistrationFee = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [banks, setBanks] = useState([]);
  const [fee, setFee] = useState(null);
  const [data, setData] = useState({
    bankname: '',
    amount: FIXED_FEE,
    payment_date: '',
    description: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [feeRes, banksRes] = await Promise.all([
          axios.get(`${url}/api/registrationfee/${id}`),
          axios.get(`${url}/api/bank`)
        ]);

        const feeData = feeRes.data.data;
        setFee(feeData);
        setData({
          bankname: feeData.bankname?._id || '',
          amount: FIXED_FEE,
          payment_date: feeData.payment_date?.split('T')[0] || '',
          description: feeData.description || ''
        });
        setBanks(banksRes.data.data || []);
      } catch (error) {
        toast.error('Failed to load data');
        navigate('/registration/fees');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!data.bankname) {
      toast.error("Please select a bank");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.put(`${url}/api/registrationfee/${id}`, {
        ...data,
        amount: FIXED_FEE
      });

      if (response.data.success) {
        toast.success("Registration fee updated successfully!");
        navigate('/registration/fees');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update registration fee");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) return <div className="loading">Loading registration fee...</div>;

  return (
    <div className="registration-container" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <button onClick={() => navigate('/registration/fees')} className="back-btn" style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={16} /> Back to List
      </button>

      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#2c3e50', fontSize: '1.5rem', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #f0f0f0' }}>
          <DollarSign size={24} /> Edit Registration Fee (Fixed: ${FIXED_FEE})
        </h2>

        {fee?.customer && (
          <div style={{ 
            background: 'linear-gradient(135deg, #667eea, #764ba2)', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            marginBottom: '2rem', 
            textAlign: 'center',
            fontSize: '1.1rem'
          }}>
            <span>Customer: <strong>{fee.customer.fullname}</strong></span>
          </div>
        )}

        {/* Fixed Fee Info */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea20, #764ba220)',
          border: '1px solid #667eea40',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#4a5568',
          fontSize: '0.95rem'
        }}>
          <Lock size={16} color="#667eea" />
          <span>Registration fee is fixed at <strong>${FIXED_FEE}</strong> and cannot be changed</span>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Fixed Fee Display - Read Only */}
          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
              Registration Fee * (Fixed)
            </label>
            <div style={{ 
              background: '#f0f0f0', 
              padding: '0.75rem', 
              borderRadius: '6px', 
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#4a5568'
            }}>
              <DollarSign size={16} color="#4caf50" />
              <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2c3e50' }}>{FIXED_FEE}.00</span>
              <Lock size={14} style={{ marginLeft: 'auto', color: '#718096' }} />
              <small style={{ color: '#718096' }}>(Fixed amount - cannot be changed)</small>
            </div>
            <input type="hidden" name="amount" value={FIXED_FEE} />
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
              Select Bank *
            </label>
            <select
              name="bankname"
              value={data.bankname}
              onChange={handleChange}
              required
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
            >
              <option value="">Select Bank</option>
              {banks.map(bank => (
                <option key={bank._id} value={bank._id}>
                  {bank.bankname}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
              Payment Date *
            </label>
            <input
              type="date"
              name="payment_date"
              value={data.payment_date}
              onChange={handleChange}
              required
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={data.description}
              onChange={handleChange}
              rows="3"
              placeholder="Enter any notes about this payment"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button
              type="submit"
              disabled={submitting}
              className="add-button"
              style={{ flex: 1, padding: '1rem' }}
            >
              {submitting ? "Updating..." : "Update Registration Fee"}
            </button>
            <button
              type="button"
              onClick={() => navigate('/registration/fees')}
              className="toggle-button"
              style={{ flex: 1, padding: '1rem' }}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Transaction Info */}
        {fee && (
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '0.875rem',
            color: '#718096'
          }}>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Receipt Number:</strong> #{fee.no}
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Created:</strong> {new Date(fee.createdAt).toLocaleString()}
            </p>
            {fee.updatedAt && fee.updatedAt !== fee.createdAt && (
              <p>
                <strong>Last Updated:</strong> {new Date(fee.updatedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditRegistrationFee;